import React from 'react';
import { useIntl } from 'react-intl';
import {
  ApiFilterOperation,
  Evidence,
  getItemFactory,
  updateItemFactory,
} from '@eas/common-web';
import { OutgoingDocument } from '@models';
import {
  DecisionState,
  DispatchState,
  DocumentType,
  EvidenceApiUrl,
  UIElement,
} from '@enums';
import { useOutgoingDocumentPermission } from '../document/document-permission';
import { DocumentToolbar } from '../document/document-toolbar';
import { useDocuments } from '../document/documents';
import { ActionBar } from './outgoing-document-actionbar';
import { useColumns } from './outgoing-document-columns';
import { Fields } from './outgoing-document-fields';
import { useValidationSchema } from './outgoing-document-schema';

export function OutgoingDocuments() {
  const intl = useIntl();
  const validationSchema = useValidationSchema();
  const columns = useColumns<OutgoingDocument>();

  const { hasPermission } = useOutgoingDocumentPermission();

  const { evidenceBag } = useDocuments({
    evidenceProps: {
      version: 17,
      apiProps: {
        url: EvidenceApiUrl.DOCUMENTS,
        getItem: getItemFactory({
          postProcess: postprocessGetCall,
        }),
        updateItem: updateItemFactory({
          preProcess: preprocessUpdateCall,
          postProcess: postprocessUpdateCall,
        }),
      },
      tableProps: {
        defaultPreFilters: [
          {
            field: 'type.id',
            operation: ApiFilterOperation.EQ,
            value: DocumentType.OUTGOING,
          },
          {
            field: 'canceledDate',
            operation: ApiFilterOperation.IS_NULL,
          },
        ],
        tableName: intl.formatMessage({
          id: 'ES__DOCUMENTS_OUTGOING__TABLE__TITLE',
          defaultMessage: 'Odchozí dokumenty',
        }),
        columns: columns,
      },
      detailProps: {
        FieldsComponent: Fields,
        validationSchema,
        toolbarProps: {
          title: intl.formatMessage({
            id: 'ES__DOCUMENTS_OUTGOING__DETAIL__TITLE',
            defaultMessage: 'Odchozí dokument',
          }),
          showBtn: (button) => !['NEW', 'REMOVE'].includes(button),
          disableBtn: (button) =>
            button === 'EDIT' &&
            !hasPermission(UIElement.OutgoingDocument.EDIT_DOCUMENT_BUTTON),
          After: DocumentToolbar,
          ActionBar,
        },
      },
    },
  });

  return <Evidence {...evidenceBag} />;
}

const postprocessGetCall = async (document: OutgoingDocument) => {
  const response = await fetch(`${EvidenceApiUrl.DOCUMENTS_DISPATCHES}/list`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      size: 0,
      filters: [
        {
          operation: ApiFilterOperation.EQ,
          value: document?.id,
          field: 'document.id',
        },
        {
          operation: ApiFilterOperation.EQ,
          value: DispatchState.PREPARED,
          field: 'state.id',
        },
      ],
    }),
  });

  const json = await response.json();

  return { ...document, preparedDispatchesCount: json.count };
};

const preprocessUpdateCall = (data: OutgoingDocument): OutgoingDocument => {
  // https://inqool.atlassian.net/browse/USCR-2686?focusedCommentId=36699
  // endpoint will ignore missing linkedDecision if it's existing and will preserve the original
  // but will fail if we are creating new one .. therefore linkedDecision: undefined here
  return { ...data, linkedDecision: undefined };
};

const postprocessUpdateCall = async (
  _: OutgoingDocument,
  document: OutgoingDocument
) => {
  if (document.decision) {
    const currentDocumentResponse = await fetch(
      `${EvidenceApiUrl.DOCUMENTS}/${document.id}`
    );
    const currentDocument =
      (await currentDocumentResponse.json()) as OutgoingDocument;

    if (currentDocument.linkedDecision?.state === DecisionState.CANCELED) {
      return document;
    }

    // https://inqool.atlassian.net/browse/USCR-2686?focusedCommentId=36699
    const method = currentDocument.linkedDecision ? 'PUT' : 'POST';

    const response = await fetch(
      `${EvidenceApiUrl.DOCUMENTS}/${document.id}/decision`,
      {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form: document.linkedDecision!.form!,
          decisionDate: document.linkedDecision!.decisionDate!,
          attachments: {
            fullTextDocx: document.linkedDecision!.attachments!.fullTextDocx!,
          },
        }),
      }
    );

    await response.json();
  }

  return document;
};
